import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

const SMOOTHING = 0.04;

export interface UseRehearsalScrollOptions {
  containerRef: RefObject<HTMLDivElement | null>;
  sectionIndex: number;
  sectionProgress: number;
  isActive: boolean;
  cursorPosition?: number;
}

export function useRehearsalScroll({
  containerRef,
  sectionIndex,
  sectionProgress,
  isActive,
  cursorPosition,
}: UseRehearsalScrollOptions): void {
  const rafRef = useRef<number | null>(null);
  const currentScrollRef = useRef(0);
  const sectionIndexRef = useRef(sectionIndex);
  const sectionProgressRef = useRef(sectionProgress);
  const cursorPositionRef = useRef(cursorPosition);

  sectionIndexRef.current = sectionIndex;
  sectionProgressRef.current = sectionProgress;
  cursorPositionRef.current = cursorPosition;

  useEffect(() => {
    if (!isActive) {
      /* v8 ignore next 4 -- cleanup already nulls the ref before re-entry */
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    function tick() {
      const container = containerRef.current;
      if (!container) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const sections = container.querySelectorAll('[data-section]') as NodeListOf<HTMLElement>;
      if (sections.length === 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // Try word-level scroll targeting first
      let targetScroll: number;
      if (cursorPositionRef.current !== undefined) {
        const wordEl = container.querySelector(
          `[data-word-index="${cursorPositionRef.current}"]`,
        ) as HTMLElement | null;
        if (wordEl) {
          targetScroll = wordEl.offsetTop - container.clientHeight / 2;
        } else {
          // Fall back to section-level
          const idx = Math.min(sectionIndexRef.current, sections.length - 1);
          const section = sections[idx];
          /* v8 ignore next 4 -- idx is clamped to valid range */
          if (!section) {
            rafRef.current = requestAnimationFrame(tick);
            return;
          }
          targetScroll =
            section.offsetTop +
            sectionProgressRef.current * section.offsetHeight -
            container.clientHeight / 2;
        }
      } else {
        const idx = Math.min(sectionIndexRef.current, sections.length - 1);
        const section = sections[idx];
        /* v8 ignore next 4 -- idx is clamped to valid range */
        if (!section) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        targetScroll =
          section.offsetTop +
          sectionProgressRef.current * section.offsetHeight -
          container.clientHeight / 2;
      }

      const clamped = Math.max(0, targetScroll);

      currentScrollRef.current += (clamped - currentScrollRef.current) * SMOOTHING;
      container.scrollTop = currentScrollRef.current;

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isActive, containerRef]);
}
